/*
 * Copyright © 2015-2019 Serenova, LLC. All rights reserved.
 */

import { selectFormInitialValues, getCurrentFormValues, getCurrentForm } from '../../form/selectors';
import { userHasAddLockPermission,userHasRemoveLockPermission,userHasDeleteLockPermission } from '../../entities/selectors';
import {
  getSelectedSubEntityId,
  getSelectedEntity,
  getCurrentEntityStore,
  getSelectedEntityId,
  getAllEntities,
} from '../selectors';
import { Map, List, fromJS } from 'immutable';
import { createSelector } from 'reselect';
import { entitiesMetaData } from '../metaData';
import { handlePendingSubmitAction, submitAmrDataForm ,submitAmrForm} from './actions';
import { Toast } from 'cx-ui-components';
import { object } from 'prop-types';
import store from '../../../store';
import { setSelectedEntityId } from '../../../modules/entities';
import { generateUUID } from 'serenova-js-utils/uuid';

export const selectAmrFormInitialValues = (state) => {
  if (getSelectedEntity(state) === undefined) {
    return new Map({
      requestId:generateUUID(),
      removeLock:false
    });
  }
  return selectAmrFormInitialValues(state);
};

export const getSubmittedValues = createSelector([getCurrentEntityStore], (entityStore) =>
  (entityStore.get('submittedValues'))!==undefined ?
    entityStore.get('submittedValues').toJSON() : 
    {}
);
export const selectViewVersionFormInitialValues = createSelector(
  [getSelectedEntity, getSelectedSubEntityId, selectAmrFormInitialValues],
  (selectedEntity, selectedSubEntityId, formInitialValues) => {
    const selectedSubEntity = selectedEntity
      .get('versions')
      .find((version) => version.get('id') === selectedSubEntityId);
    if (selectedSubEntity) {
      return fromJS({
        artifactTypeId: selectedEntity.get('artifactTypeId'),
        artifactTypeName: selectedEntity.get('artifactTypeName'),
        artifactType: selectedEntity.get('artifactType'),
        artifactSubtype: selectedEntity.get('artifactSubtype'),
        name: selectedSubEntity.get('name'),
        description: selectedSubEntity.get('description'),
        after: selectedSubEntity.get('after'),
        action: selectedSubEntity.get('action'),
      });
    }
    return formInitialValues;
  }
);

export const selectCreateVersionFormInitialValues = createSelector(
  [selectAmrFormInitialValues],
  (alpInitialValues) => {
    return alpInitialValues.merge({
      action: '',
      after: '',
      name: '',
      description: '',
    });
  }
);

export const selectTermsFormInitialValues = (state) => {
  return new Map({
    storageLimitCosts: false,
    billingPerGB: false,
    retrieveFromStorage: false,
    deletedArtifacts: false,
  });
};

export const getCurrentParentFormValues = createSelector([getCurrentFormValues], (currentFormValues) =>
  currentFormValues!==undefined?currentFormValues.toJS():{}
);

export const selectAlpsVersions = createSelector(
  [getSelectedEntity],
  (entity) => (entity ? [] : [])
);

export const isTermsFormOpen = createSelector([getCurrentEntityStore], (entityStore) =>
  entityStore.get('termsFormOpen')
);
export const getViewData = createSelector([getCurrentEntityStore, getSubmittedValues], (entityStore, isProgressiveView) => {
  if (entityStore.get('viewData') !== undefined) { return entityStore.get('viewData').toJSON(); }
  else {
    return [];
  }
}
);

export const getProcessedViewData = createSelector([getViewData], (viewData) => //{

  viewData.map(data => {
    /*artifactFilesProcessed means Total Files needs to be processed, If it is 0 means data processing is complete*/
    if (data.completionStatus.toLowerCase() == "completed") {
      data.complete = "Yes"
    } else if (data.completionStatus.toLowerCase().trim() == "inprogress" || data.completionStatus.toLowerCase().trim() == "error") {
      data.complete = "No"
    }
    return data;
  })
);

export const getDisplayDataRaw = createSelector([getViewData], (viewData) => {
    const [initialValues] = viewData; 
    return initialValues;
});

export const getDisplayData = createSelector([getViewData], (viewData) => {
  if (viewData.length > 0) {
    const [initialValues] = viewData; 
    return initialValues;
  } else {
    const initialValues = {
      description: '',
      requestType:'',
      artifactTypeStr:''

    }
    return initialValues;
  }
});


export const getStatus = createSelector([getViewData], (viewData) => {
  const [displayD] = viewData;
  return displayD !== undefined ? displayD.completionStatus : "";
});

export const getArtifactFailedDeleted = createSelector([getViewData], (viewData) => 
viewData.reduce((acc, obj) => { return acc + obj.artifactsFailedDeleted }, 0).toString())

export const getArtifactFailedLocked = createSelector([getViewData], (viewData) => 
 viewData.reduce((acc, obj) => { return acc + obj.artifactsFailedLocked }, 0).toString())

export const getArtifactsProcessedCount =  createSelector([getViewData], (viewData) => 
viewData.reduce((acc, obj) => { return acc + obj.artifactsProcessed }, 0).toString())

const validInteractionProcessedStatus=["Yes","No","in progress"];
export const getInteractionsProcessedCount =  createSelector([getViewData], (viewData) => 
   viewData.filter(rec=>{
      return validInteractionProcessedStatus.includes(rec.complete)
  }).length.toString()
);

const invalidInteractionProcessedStatus=["error","cancelled"];
export const getInteractionsRemainingCount =  createSelector([getViewData], (viewData) => 
   viewData.filter(rec=>{
      return invalidInteractionProcessedStatus.includes(rec.complete)
  }).length.toString()
);

const ErrorStatus=["error"];
export const getErrorCount =  createSelector([getViewData], (viewData) => 
   viewData.filter(rec=>{
      return ErrorStatus.includes(rec.completionStatus.toLowerCase())
  }).length.toString()
);

export const getAnswerIfAPIProcessingHasFinished = createSelector([getSubmittedValues, getViewData], (submittedValues, viewData) => {
  if (submittedValues.interactionIds) {
    const submittedInteractionIds = submittedValues.interactionIds.split(',').length;
    if (submittedInteractionIds == viewData.length) {
      store.dispatch({
        type: "FETCH_DATA_AMR",
        entityName: "amr",
        requestId:submittedValues.requestId

      })
    }
  }
});

export const getPendingSubmit = createSelector([getCurrentEntityStore], (entityStore) =>
  entityStore.get('pendingSubmit')
);

export const onSubmitClick = (values, dispatch, props) => {
  return dispatch(submitAmrDataForm(values));
};

export const submitAmrFormData = (values, dispatch, props) => {
  return dispatch(submitAmrForm(values));
};

export const getSelectableVersionList = createSelector(
  [getSelectedEntity],
  (selectedEntity) =>
    selectedEntity &&
    selectedEntity.get('versions') &&
    selectedEntity
      .get('versions')
      .map((version) => ({ value: version.get('id'), label: `${version.get('version')} - ${version.get('name')}` }))
);

export const isCreatingEntity = createSelector(
  [getSelectedEntityId],
  (selectedEntityId) => selectedEntityId === 'create'
);

export const selectTypes = createSelector([getCurrentEntityStore], (entityStore) => {
  return entityStore.get('artifactTypes')!==undefined? entityStore.get('artifactTypes'):[]});
  

  export const requestType = createSelector([getCurrentEntityStore], (entityStore) => {
    let state=store.getState();
    return entityStore.get('requestType').filter(type => {
        if(type.requestType=="add-lock" &&  userHasAddLockPermission(state)==true ){
            return type;
        }
        else if(type.requestType=="remove-lock" && userHasRemoveLockPermission(state) ==true){
          return type;
        }
        else if(type.requestType=="delete" && userHasDeleteLockPermission(state)){
          return type;
        }
    });
  
  });

export const selectTypesForDisplay = createSelector([selectTypes], (types) =>
 { 
   return types.map((type) => ({
    label: type.artifactTypeName,
    value: type.artifactTypeId,
  }))
}
);


export const selectRequestTypesForDisplay = createSelector([requestType], (types) =>
 {
   return types.map((type) => ({
    label: type.requestTypeName,
    value: type.requestType,
  }))
}
);

const findLabelForValue = (value, labelList) => {
  const foundObj = labelList && labelList.find((labelObj) => labelObj.value === value);
  return foundObj && foundObj.label;
};

export const selectTypeLabel = createSelector([getSelectedEntity], (alp) => alp.get('artifactTypeName'));

export const selectSubtypeLabel = createSelector([getSelectedEntity], (alp) =>
  findLabelForValue(alp.get('artifactSubtype'), entitiesMetaData.alps.artifactSubtypes)
);

const typeHasSubtypes = (types, selectedArtifactType) => {
  const matchingType = types.find((type) => type.artifactTypeName === selectedArtifactType);
  return matchingType && matchingType.hasSubtypes;
};

export const valueTypeHasSubtypes = (state, searchType) => {
  return typeHasSubtypes(selectTypes(state), searchType);
};

export const formTypeHasSubtypes = createSelector([getCurrentForm, selectTypes],
   (currentForm, types) => {
  const selectedArtifactType = currentForm && currentForm.getIn(['values', 'requestedArtifactTypeId']);
 return types.find(type=>{
      if(type.artifactTypeId==selectedArtifactType && entitiesMetaData.alps.typesWithSubtypes.includes(type.artifactTypeName)){
        return typeHasSubtypes(types, type.artifactTypeName);
      }
  })
});

export const formSelectedRequestType = createSelector([getCurrentForm, selectTypes], (currentForm, types) => {
  const selectedArtifactType = currentForm && currentForm.getIn(['values', 'requestType']);
  return selectedArtifactType;
});
